// hooks/useUserActivity.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { debounce } from 'lodash';

export const useUserActivity = ({ timeout, onActivity, enabled = true }) => {
  const [isUserActive, setIsUserActive] = useState(false);
  const timeoutRef = useRef(null);
  const activityHandledRef = useRef(false);

  // Debounced activity handler to prevent excessive calls
  const debouncedActivityHandler = useCallback(
    debounce(() => {
      if (!activityHandledRef.current && enabled) {
        // console.log('activity');
        setIsUserActive(true);
        activityHandledRef.current = true;
        onActivity?.();
      }
    }, 100),
    [onActivity, enabled]
  );

  const resetActivity = useCallback(() => {
    setIsUserActive(false);
    activityHandledRef.current = false;
  }, []);

  useEffect(() => {
    if (!enabled || !timeout) return;

    // Set initial timeout
    timeoutRef.current = setTimeout(() => {
      // Add event listeners after timeout
      const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

      events.forEach((event) => {
        document.addEventListener(event, debouncedActivityHandler, { passive: true });
      });

      // Cleanup event listeners after first activity or component unmount
      const cleanup = () => {
        events.forEach((event) => {
          document.removeEventListener(event, debouncedActivityHandler);
        });
      };

      return cleanup;
    }, timeout);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      debouncedActivityHandler.cancel();
    };
  }, [timeout, enabled, debouncedActivityHandler]);

  return { isUserActive, resetActivity };
};
